import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";
import { AuthButton, AuthInput } from "../../auth/style";
import { AppContext } from "../../context";
import {
  formatDateTimeWithoutYear,
  getRelativeTime,
} from "../../common/DateHelpers";
import { formatStoreOrderLabel } from "./storeSalesDashboardHelpers";
import { createStoreInvite, getDealerInvites } from "../service";
import {
  getDealerBuyers,
  getDealerOrders,
  getStoreShipmentInvoice,
  updateStoreProjectStatus,
} from "../sales/service";
import StoreShipmentInvoiceModal from "../components/StoreShipmentInvoiceModal";
import DealerOrderCard from "../components/DealerOrderCard";
import DealerOrderReviewModal from "../components/DealerOrderReviewModal";
import CreditNoteListSection from "../creditNotes/components/CreditNoteListSection";
import { PARTY_SCOPE } from "../creditNotes/apiContract";
import { enrichProjectListCreditMock } from "../creditNotes/fixtures";
import "./StoreSalesDashboard.css";

const fmtDate = (value) => {
  if (!value) return "-";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toErrorText = (err, fallback) => {
  const candidate = err?.error ?? err?.message ?? fallback;

  if (typeof candidate === "string") return candidate;

  if (candidate && typeof candidate === "object") {
    if (typeof candidate.message === "string") return candidate.message;

    try {
      return JSON.stringify(candidate);
    } catch {
      return fallback;
    }
  }

  return fallback;
};

const digitsOnly = (s) => String(s || "").replace(/\D/g, "");

/**
 * Strips non-digits; for pastes like "+91 87544 64 398" returns "8754464398".
 * Keeps partial input (under 10 digits) as-is for typing.
 */
const normalizeInviteMobileInput = (raw) => {
  const d = digitsOnly(raw);
  if (d.length === 0) return "";
  if (d.length <= 10) return d;
  if (d.startsWith("91") && d.length >= 12) {
    const national = d.slice(2, 12);
    if (national.length === 10) return national;
  }
  return d.slice(-10);
};

const isValidTenDigitMobile = (value) => /^\d{10}$/.test(String(value || ""));

const isValidInviteCustomerName = (value) =>
  String(value || "").trim().length >= 2;

const fmtCurrency = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "-";
  return `Rs ${num.toLocaleString("en-IN")}`;
};

const fmtHoverDateTime = (value) => formatDateTimeWithoutYear(value);

const fmtOrderRupee = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "—";
  return `₹${num.toLocaleString("en-IN")}`;
};

const formatStoreProjectStatusWord = (status) => {
  const s = String(status || "").trim();
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const fmtProjectScore = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return 0;
  return num;
};

const tsOrZero = (value) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

const buildWhatsAppDealerInviteUrl = (mobile10, customerName, link) => {
  const name = String(customerName || "").trim() || "there";
  const text = `Hi ${name},\n\nCheck out my ZeroBuild store here. Modular Carcass units delivered in 1 day.\n\n${link}`;
  const digits = String(mobile10 || "").replace(/\D/g, "");
  if (digits.length !== 10) return null;
  return `https://wa.me/91${digits}?text=${encodeURIComponent(text)}`;
};

const BuyerDetailsPane = ({ buyer, onProjectClick }) => {
  const users = Array.isArray(buyer?.users) ? buyer.users : [];
  const projects = Array.isArray(buyer?.projects) ? buyer.projects : [];
  const [usersOpen, setUsersOpen] = useState(false);
  const [userSortConfig, setUserSortConfig] = useState({
    key: "last_activity_at",
    direction: "desc",
  });
  const [projectSortConfig, setProjectSortConfig] = useState({
    key: "last_activity_at",
    direction: "desc",
  });

  const renderSortIcon = (cfg, key) =>
    cfg.key !== key ? "⇅" : cfg.direction === "asc" ? "▲" : "▼";

  const handleUserSort = (key) =>
    setUserSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  const handleProjectSort = (key) =>
    setProjectSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const sortedUsers = [...users].sort((a, b) => {
    const factor = userSortConfig.direction === "asc" ? 1 : -1;
    if (userSortConfig.key === "name" || userSortConfig.key === "mobile") {
      return (
        String(a?.[userSortConfig.key] || "").localeCompare(
          String(b?.[userSortConfig.key] || "")
        ) * factor
      );
    }
    return (
      (tsOrZero(a?.last_activity_at) - tsOrZero(b?.last_activity_at)) * factor
    );
  });

  const sortedProjects = [...projects].sort((a, b) => {
    const factor = projectSortConfig.direction === "asc" ? 1 : -1;
    if (
      projectSortConfig.key === "name" ||
      projectSortConfig.key === "status"
    ) {
      return (
        String(a?.[projectSortConfig.key] || "").localeCompare(
          String(b?.[projectSortConfig.key] || "")
        ) * factor
      );
    }
    if (
      projectSortConfig.key === "value" ||
      projectSortConfig.key === "score"
    ) {
      return (
        (Number(a?.[projectSortConfig.key] || 0) -
          Number(b?.[projectSortConfig.key] || 0)) *
        factor
      );
    }
    return (
      (tsOrZero(a?.last_activity_at) - tsOrZero(b?.last_activity_at)) * factor
    );
  });

  if (!buyer) return null;

  return (
    <>
      <div className="mb-3 position-relative">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <h6 className="fw-bold mb-1">
            {buyer.name} <span className="text-muted">#{buyer.id}</span>
          </h6>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            aria-label="Show users"
            onClick={() => setUsersOpen((prev) => !prev)}
          >
            <i className="fa-solid fa-user" aria-hidden />
          </button>
        </div>
        {usersOpen ? (
          <div className="store-sales-users-popover shadow">
            <div className="store-sales-users-popover__header">
              <span>Users</span>
              <button
                type="button"
                className="btn-close btn-close-white"
                aria-label="Close"
                onClick={() => setUsersOpen(false)}
              />
            </div>
            <Table
              bordered
              responsive
              size="sm"
              className="mb-0 store-sales-table"
            >
              <thead className="store-sales-table__head">
                <tr>
                  <th role="button" onClick={() => handleUserSort("name")}>
                    Name {renderSortIcon(userSortConfig, "name")}
                  </th>
                  <th role="button" onClick={() => handleUserSort("mobile")}>
                    Mobile {renderSortIcon(userSortConfig, "mobile")}
                  </th>
                  <th
                    role="button"
                    onClick={() => handleUserSort("last_activity_at")}
                  >
                    Last Activity{" "}
                    {renderSortIcon(userSortConfig, "last_activity_at")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-muted text-center">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.mobile || "-"}</td>
                      <td>{getRelativeTime(user.last_activity_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        ) : null}
      </div>

      <div className="mb-3">
        <h6 className="fw-semibold mb-2">Projects</h6>
        <Table bordered responsive size="sm" className="mb-0 store-sales-table">
          <thead className="store-sales-table__head">
            <tr>
              <th role="button" onClick={() => handleProjectSort("name")}>
                Name (#id) {renderSortIcon(projectSortConfig, "name")}
              </th>
              <th role="button" onClick={() => handleProjectSort("value")}>
                Value {renderSortIcon(projectSortConfig, "value")}
              </th>
              <th role="button" onClick={() => handleProjectSort("status")}>
                Status {renderSortIcon(projectSortConfig, "status")}
              </th>
              <th
                role="button"
                onClick={() => handleProjectSort("last_activity_at")}
              >
                Last Activity{" "}
                {renderSortIcon(projectSortConfig, "last_activity_at")}
              </th>
              <th role="button" onClick={() => handleProjectSort("score")}>
                Score {renderSortIcon(projectSortConfig, "score")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedProjects.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted text-center">
                  No projects found.
                </td>
              </tr>
            ) : (
              sortedProjects.map((project) => (
                <tr key={project.id}>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={() => onProjectClick(project)}
                    >
                      {formatStoreOrderLabel(project)}
                    </button>
                  </td>
                  <td>{fmtCurrency(project.value)}</td>
                  <td className="text-capitalize">{project.status || "-"}</td>
                  <td>{getRelativeTime(project.last_activity_at)}</td>
                  <td className="fw-semibold">
                    {fmtProjectScore(project.score)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const DealerDashboard = () => {
  const { dealerReferral } = useContext(AppContext);
  const [inviteMessage, setInviteMessage] = useState("");
  /** 'modal' after Copy in invite success; row key for pending-invites table copy */
  const [copyFlash, setCopyFlash] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteMobile, setInviteMobile] = useState("");
  const [createdInviteLink, setCreatedInviteLink] = useState("");
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
  const [inviteProceedAttempted, setInviteProceedAttempted] = useState(false);
  const [invites, setInvites] = useState([]);
  const [isInvitesLoading, setIsInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState("");
  const [buyers, setBuyers] = useState([]);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [buyersError, setBuyersError] = useState("");
  const [mainTab, setMainTab] = useState("customers");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [invoiceModal, setInvoiceModal] = useState({
    isOpen: false,
    loading: false,
    error: null,
    shipmentLabel: "",
    invoice: null,
  });
  const [acceptingProjectId, setAcceptingProjectId] = useState(null);
  const [reviewModal, setReviewModal] = useState({
    isOpen: false,
    projectId: null,
    projectName: "",
    meta: null,
    priceMeta: null,
    buyerPayable: null,
    dealerPayable: null,
    margin: null,
    creditAdjustmentsTotal: 0,
    showAcceptButton: false,
  });
  const [highlightBuyerId, setHighlightBuyerId] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    () => window.innerWidth < 768
  );
  const shareCode = String(dealerReferral?.dealer?.code || "").trim();
  const dealerId = dealerReferral?.dealer?.id ?? null;
  const genericLink = shareCode
    ? `${window.location.origin}/d/${shareCode}`
    : "";

  const copyText = async (value) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      return true;
    } catch (_error) {
      return false;
    }
  };

  const handleViewInviteRowLink = (invite) => {
    if (invite == null) return;
    const mobile = String(invite.mobile ?? "").trim();
    const name = String(invite.name ?? "").trim();
    const link = `${genericLink}?mobile=${encodeURIComponent(mobile)}`;
    setInviteName(name);
    setInviteMobile(mobile);
    setInviteMessage("");
    setCopyFlash(null);
    setCreatedInviteLink(link);
    setIsInviteModalOpen(true);
  };

  const loadInvites = useCallback(async () => {
    if (!dealerId) {
      setInvites([]);
      return;
    }

    setIsInvitesLoading(true);
    setInvitesError("");

    try {
      const response = await getDealerInvites(dealerId);
      setInvites(response.data);
    } catch (e) {
      setInvites([]);
      setInvitesError("Unable to load invites.");
    } finally {
      setIsInvitesLoading(false);
    }
  }, [dealerId]);

  const loadBuyers = useCallback(async () => {
    setBuyersLoading(true);
    setBuyersError("");
    try {
      const payload = await getDealerBuyers();
      setBuyers(Array.isArray(payload?.buyers) ? payload.buyers : []);
    } catch (e) {
      setBuyers([]);
      setBuyersError(toErrorText(e, "Unable to load dealer buyers."));
    } finally {
      setBuyersLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError("");
    try {
      const payload = await getDealerOrders();
      const list = Array.isArray(payload?.orders) ? payload.orders : [];
      setOrders(enrichProjectListCreditMock(list));
    } catch (e) {
      setOrdersError(toErrorText(e, "Unable to load orders."));
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  useEffect(() => {
    loadBuyers();
  }, [loadBuyers]);

  useEffect(() => {
    if (mainTab !== "orders") return;
    loadOrders();
  }, [mainTab, loadOrders]);

  const openOrderReviewModal = (row, { showAcceptButton = false } = {}) => {
    const pid = row?.id;
    if (pid == null) return;
    const pname = String(row?.name || "").trim() || "Untitled project";
    setReviewModal({
      isOpen: true,
      projectId: pid,
      projectName: pname,
      meta: row?.dealer_price_meta ?? row?.meta ?? null,
      priceMeta: row?.price_meta ?? null,
      buyerPayable: row?.buyer_payable_total ?? null,
      dealerPayable: row?.payable_total ?? null,
      margin: row?.margin ?? null,
      creditAdjustmentsTotal: row?.credit_adjustments_total ?? 0,
      showAcceptButton,
    });
  };

  const closeOrderReviewModal = () => {
    setReviewModal({
      isOpen: false,
      projectId: null,
      projectName: "",
      meta: null,
      priceMeta: null,
      buyerPayable: null,
      dealerPayable: null,
      margin: null,
      creditAdjustmentsTotal: 0,
      showAcceptButton: false,
    });
  };

  const handleAcceptOrder = async (projectId) => {
    const id = Number(projectId);
    if (!Number.isFinite(id) || id <= 0) return;
    if (acceptingProjectId != null) return;
    setOrdersError("");
    setAcceptingProjectId(id);
    try {
      await updateStoreProjectStatus(id, "dealer_accepted");
      closeOrderReviewModal();
      await loadOrders();
    } catch (e) {
      setOrdersError(toErrorText(e, "Unable to accept order."));
    } finally {
      setAcceptingProjectId(null);
    }
  };

  const openShipmentInvoiceModal = async (shipment) => {
    const shipmentId = shipment?.id;

    if (shipmentId == null) return;

    setOrdersError("");
    setInvoiceModal({
      isOpen: true,
      loading: true,
      error: null,
      shipmentLabel: String(shipment?.name || "").trim() || "Shipment",
      invoice: null,
    });
    try {
      const invoice = (await getStoreShipmentInvoice(shipmentId)).res;

      setInvoiceModal((prev) => ({
        ...prev,
        loading: false,
        invoice,
        error: null,
      }));
    } catch (e) {
      setInvoiceModal((prev) => ({
        ...prev,
        loading: false,
        error: toErrorText(e, "Unable to load invoice."),
        invoice: null,
      }));
    }
  };

  const closeShipmentInvoiceModal = () => {
    setInvoiceModal({
      isOpen: false,
      loading: false,
      error: null,
      shipmentLabel: "",
      invoice: null,
    });
  };

  useEffect(() => {
    if (highlightBuyerId == null) return undefined;
    const t = window.setTimeout(() => setHighlightBuyerId(null), 2600);
    return () => window.clearTimeout(t);
  }, [highlightBuyerId]);

  useEffect(() => {
    if (highlightBuyerId == null) return undefined;
    if (mainTab !== "customers") return undefined;
    if (buyersLoading) return undefined;

    const id = window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-store-dashboard-buyer-row="${highlightBuyerId}"]`
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [highlightBuyerId, mainTab, buyersLoading, buyers.length]);

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (copyFlash == null) return undefined;
    const t = setTimeout(() => setCopyFlash(null), 2000);
    return () => clearTimeout(t);
  }, [copyFlash]);

  const openInviteModal = () => {
    setInviteName("");
    setInviteMobile("");
    setInviteMessage("");
    setCreatedInviteLink("");
    setInviteProceedAttempted(false);
    setCopyFlash(null);
    setIsInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    if (isSubmittingInvite) return;
    setIsInviteModalOpen(false);
  };

  const handleInviteSubmit = async () => {
    setInviteProceedAttempted(true);
    setInviteMessage("");

    const mobile = normalizeInviteMobileInput(inviteMobile);
    const name = String(inviteName || "").trim();

    if (!isValidInviteCustomerName(name) || !isValidTenDigitMobile(mobile)) {
      return;
    }

    if (!dealerId) {
      setInviteMessage("Dealer is unavailable for this invite.");
      return;
    }

    setIsSubmittingInvite(true);

    try {
      const response = await createStoreInvite({
        mobile,
        name,
        store_dealer_id: dealerId,
      });
      const createdInvite = response.data;

      setInvites((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        const createdId = createdInvite?.id;
        if (
          createdId != null &&
          current.some((row) => row?.id != null && row.id === createdId)
        ) {
          return current;
        }
        return [createdInvite, ...current];
      });

      const createdMobile = String(createdInvite?.mobile || mobile).trim();
      const nextInviteLink =
        genericLink && createdMobile
          ? `${genericLink}?mobile=${encodeURIComponent(createdMobile)}`
          : "";

      setCreatedInviteLink(nextInviteLink);
      setInviteMessage("");
    } catch (e) {
      setInviteMessage(toErrorText(e, "Unable to send invite."));
    } finally {
      setIsSubmittingInvite(false);
    }
  };

  const handleCopyCreatedInviteLink = async () => {
    if (!createdInviteLink) {
      setInviteMessage("Invite link is unavailable.");
      return;
    }
    const copied = await copyText(createdInviteLink);
    if (copied) {
      setCopyFlash("modal");
      return;
    }
    setInviteMessage("Unable to copy invite link.");
  };

  const handleShareInviteWhatsApp = () => {
    if (!createdInviteLink) return;
    const m = normalizeInviteMobileInput(inviteMobile);
    const url = buildWhatsAppDealerInviteUrl(m, inviteName, createdInviteLink);
    if (!url) return;
    const popup = window.open(url, "_blank");
    // On desktop browsers, wa.me can leave a blank helper tab after handing off to WhatsApp.
    // Best-effort close after launch (safe no-op if blocked/already closed).
    if (popup) {
      setTimeout(() => {
        try {
          popup.close();
        } catch (_e) {
          // ignore
        }
      }, 1500);
    }
  };

  const hasPendingInvites =
    !isInvitesLoading &&
    !invitesError &&
    Array.isArray(invites) &&
    invites.length > 0;

  const isCustomersEmpty =
    !buyersLoading &&
    !buyersError &&
    Array.isArray(buyers) &&
    buyers.length === 0;

  const handleOpenBuyer = (buyer) => {
    if (!buyer) return;
    setSelectedBuyer(buyer);
    setIsDetailsOpen(true);
  };

  const handleCloseBuyer = () => {
    setIsDetailsOpen(false);
    setSelectedBuyer(null);
  };

  const openCustomersTabWithBuyerHighlight = (buyerIdRaw) => {
    const buyerId = Number(buyerIdRaw);
    if (!Number.isFinite(buyerId) || buyerId <= 0) return;
    setMainTab("customers");
    setHighlightBuyerId(buyerId);
  };

  const handleProjectClick = (project) => {
    if (!project?.id) return;
    window.open(`/projects/${project.id}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#eef2f6" }}>
      <div className="container-fluid p-3">
        {hasPendingInvites ? (
          <>
            <h4 className="fw-bold mb-3">Pending Invites</h4>
            <div className="table-responsive mt-3">
              <table className="table table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Sent</th>
                    <th>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((invite, index) => (
                    <tr
                      key={
                        invite.id != null
                          ? String(invite.id)
                          : `invite-${index}-${String(invite.mobile ?? "")}`
                      }
                    >
                      <td>
                        <div className="fw-bold">{invite.name || "—"}</div>
                        <div className="text-muted small">
                          {invite.mobile || "—"}
                        </div>
                      </td>
                      <td className="text-capitalize">
                        {invite.status || "—"}
                      </td>
                      <td>{getRelativeTime(invite.created_at)}</td>
                      <td className="text-center">
                        <div className="d-inline-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={() => handleViewInviteRowLink(invite)}
                            aria-label="View invite link"
                            title="View invite link"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        <div
          className={`store-dashboard-tabbed${
            hasPendingInvites ? " mt-4" : ""
          }`}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-end gap-2">
            <Nav
              tabs
              className="store-dashboard-nav-tabs mb-0 flex-grow-1 min-w-0"
            >
              <NavItem>
                <NavLink
                  href="#"
                  className="fw-bold"
                  active={mainTab === "customers"}
                  onClick={(e) => {
                    e.preventDefault();
                    setMainTab("customers");
                  }}
                >
                  My Customers
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className="fw-bold"
                  active={mainTab === "orders"}
                  onClick={(e) => {
                    e.preventDefault();
                    setMainTab("orders");
                  }}
                >
                  My Orders
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className="fw-bold"
                  active={mainTab === "credit_notes"}
                  onClick={(e) => {
                    e.preventDefault();
                    setMainTab("credit_notes");
                  }}
                >
                  Credit Notes
                </NavLink>
              </NavItem>
            </Nav>
            {(!isCustomersEmpty || hasPendingInvites) && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary flex-shrink-0 mb-1"
                onClick={openInviteModal}
              >
                Invite Customers
              </button>
            )}
          </div>

          <div className="store-dashboard-tab-panel">
            {mainTab === "credit_notes" ? (
              <CreditNoteListSection
                partyScope={PARTY_SCOPE.DEALER}
                detailLinkPrefix="/dealer/credit-notes"
              />
            ) : mainTab === "orders" ? (
              ordersError ? (
                <Alert color="danger" className="mb-0" fade={false}>
                  {ordersError}
                </Alert>
              ) : ordersLoading ? (
                <div className="py-4 text-center">
                  <Spinner />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-muted text-center py-4">
                  No orders yet.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {orders.map((row) => (
                    <DealerOrderCard
                      key={row?.id ?? row?.name}
                      row={row}
                      acceptingProjectId={acceptingProjectId}
                      onBuyerClick={openCustomersTabWithBuyerHighlight}
                      onReviewOrder={openOrderReviewModal}
                      onOpenShipmentInvoice={openShipmentInvoiceModal}
                    />
                  ))}
                </div>
              )
            ) : buyersError ? (
              <Alert color="danger" className="mb-0" fade={false}>
                {buyersError}
              </Alert>
            ) : buyersLoading ? (
              <div className="py-4 text-center">
                <Spinner />
              </div>
            ) : isCustomersEmpty && !hasPendingInvites ? (
              <div
                className="py-5 px-3 text-center"
                style={{ maxWidth: 480, margin: "0 auto" }}
              >
                <p className="text-muted mb-2">
                  Invite your customers to try ZeroBuild.
                </p>
                <p className="text-muted mb-4">It just takes a minute.</p>
                <Button color="primary" onClick={openInviteModal}>
                  Invite Customers
                </Button>
              </div>
            ) : isCustomersEmpty && hasPendingInvites ? (
              <div
                className="py-5 px-3 text-center"
                style={{ maxWidth: 480, margin: "0 auto" }}
              >
                <p className="text-muted mb-0">
                  No customers have signed up yet.
                </p>
              </div>
            ) : (
              <Table bordered hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Signed up</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {buyers.map((row) => (
                    <tr
                      key={row?.id ?? `${row?.name}-${row?.created_at}`}
                      data-store-dashboard-buyer-row={row?.id ?? ""}
                      className={
                        highlightBuyerId != null &&
                        row?.id != null &&
                        highlightBuyerId === Number(row.id)
                          ? "store-dashboard-buyer-row--highlight"
                          : undefined
                      }
                      onClick={() => handleOpenBuyer(row)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{row?.name || row?.users[0]?.mobile} </td>
                      <td>{fmtDate(row?.created_at)}</td>
                      <td>{getRelativeTime(row?.last_activity_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={isInviteModalOpen} toggle={closeInviteModal}>
        <ModalHeader toggle={closeInviteModal}>Invite Customers</ModalHeader>
        <ModalBody>
          {!createdInviteLink ? (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleInviteSubmit();
              }}
            >
              <FormGroup noMargin className="mb-3">
                <AuthInput
                  $compact
                  invalid={
                    inviteProceedAttempted &&
                    !isValidInviteCustomerName(inviteName)
                  }
                  type="text"
                  value={inviteName}
                  placeholder="Customer Name"
                  onChange={(e) => setInviteName(e.target.value)}
                  disabled={isSubmittingInvite}
                  autoComplete="name"
                  name="customerName"
                  maxLength={200}
                  className="unmask"
                />
                {inviteProceedAttempted &&
                !isValidInviteCustomerName(inviteName) ? (
                  <FormFeedback>
                    Name must contain at least 2 characters.
                  </FormFeedback>
                ) : null}
              </FormGroup>

              <FormGroup noMargin className="mb-3">
                <AuthInput
                  $compact
                  invalid={
                    inviteProceedAttempted &&
                    (inviteMobile.length === 0 ||
                      !isValidTenDigitMobile(inviteMobile))
                  }
                  type="tel"
                  value={inviteMobile}
                  placeholder="Customer Mobile Number"
                  onChange={(e) =>
                    setInviteMobile(
                      normalizeInviteMobileInput(e.target.value).slice(0, 10)
                    )
                  }
                  disabled={isSubmittingInvite}
                  autoComplete="tel"
                  name="customerMobile"
                  maxLength={10}
                  className="unmask"
                />
                <div className="d-block mt-2 pb-1 text-subtle small">
                  Enter 10 digits. +91 or spaces are allowed.
                </div>
                {inviteProceedAttempted &&
                !isValidTenDigitMobile(inviteMobile) ? (
                  <FormFeedback>
                    Please enter a valid 10 digit mobile number.
                  </FormFeedback>
                ) : null}
              </FormGroup>

              {inviteMessage ? (
                <Alert color="danger" className="mb-3">
                  {inviteMessage}
                </Alert>
              ) : null}

              <div className="d-grid gap-2">
                <AuthButton
                  type="submit"
                  disabled={isSubmittingInvite}
                  color="primary"
                >
                  {isSubmittingInvite ? (
                    <>
                      <Spinner size="sm">Loading...</Spinner>{" "}
                      <span>Sending...</span>
                    </>
                  ) : (
                    "Proceed"
                  )}
                </AuthButton>
                <button
                  type="button"
                  className="btn btn-link text-decoration-none"
                  onClick={closeInviteModal}
                  disabled={isSubmittingInvite}
                >
                  Cancel
                </button>
              </div>
            </Form>
          ) : (
            <>
              <p className="text-muted small mb-2">
                Share the link with your customer.
              </p>
              <div className="bg-light border rounded p-2 text-muted small text-break">
                {createdInviteLink}
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <Button
                  type="button"
                  color="primary"
                  onClick={handleCopyCreatedInviteLink}
                >
                  Copy Link
                </Button>
                <Button
                  type="button"
                  color="outline-secondary"
                  onClick={handleShareInviteWhatsApp}
                >
                  Share in WhatsApp
                </Button>
                {copyFlash === "modal" ? (
                  <span className="text-success small">Copied</span>
                ) : null}
              </div>
              {inviteMessage ? (
                <div className="small text-danger mt-2" role="alert">
                  {inviteMessage}
                </div>
              ) : null}
            </>
          )}
        </ModalBody>
      </Modal>

      <DealerOrderReviewModal
        isOpen={reviewModal.isOpen}
        projectId={reviewModal.projectId}
        projectName={reviewModal.projectName}
        meta={reviewModal.meta}
        priceMeta={reviewModal.priceMeta}
        buyerPayable={reviewModal.buyerPayable}
        dealerPayable={reviewModal.dealerPayable}
        margin={reviewModal.margin}
        creditAdjustmentsTotal={reviewModal.creditAdjustmentsTotal}
        showAcceptButton={reviewModal.showAcceptButton}
        onClose={closeOrderReviewModal}
        onAccept={handleAcceptOrder}
        accepting={
          acceptingProjectId != null &&
          Number(acceptingProjectId) === Number(reviewModal.projectId)
        }
      />

      <StoreShipmentInvoiceModal
        isOpen={invoiceModal.isOpen}
        toggle={closeShipmentInvoiceModal}
        shipmentLabel={invoiceModal.shipmentLabel}
        loading={invoiceModal.loading}
        error={invoiceModal.error}
        invoice={invoiceModal.invoice}
      />

      {!isMobileView ? (
        <Offcanvas
          isOpen={isDetailsOpen}
          toggle={handleCloseBuyer}
          direction="end"
          style={{ width: "52%", minWidth: 420 }}
          className="border-start store-sales-offcanvas"
        >
          <OffcanvasHeader toggle={handleCloseBuyer}>
            Customer Details
          </OffcanvasHeader>
          <OffcanvasBody>
            <BuyerDetailsPane
              buyer={selectedBuyer}
              onProjectClick={handleProjectClick}
            />
          </OffcanvasBody>
        </Offcanvas>
      ) : (
        <Modal isOpen={isDetailsOpen} toggle={handleCloseBuyer} fullscreen>
          <ModalHeader toggle={handleCloseBuyer}>Customer Details</ModalHeader>
          <ModalBody>
            <BuyerDetailsPane
              buyer={selectedBuyer}
              onProjectClick={handleProjectClick}
            />
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default DealerDashboard;
